import React, { useState, useEffect } from 'react';
import { Button } from '../ui/Button/Button';
import { apiClient } from '../../api/client';
import type { ProjectInfo } from '../../types/merge';
import type { DashboardResponse } from '../../types';

interface ProjectSelectionProps {
  onSelect: (projectIds: [string, string], projectTitles: [string, string]) => void;
  onCancel?: () => void;
  selectedProjects?: [string, string] | null;
}

export const ProjectSelection: React.FC<ProjectSelectionProps> = ({
  onSelect,
  onCancel,
  selectedProjects
}) => {
  const [projects, setProjects] = useState<ProjectInfo[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>(selectedProjects ? [...selectedProjects] : []);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const dashboardData: DashboardResponse = await apiClient.getDashboard();
      
      // 進行中と完了済みの両方のプロジェクトを含める
      const allExaminations = [
        ...dashboardData.inProgressExaminations,
        ...dashboardData.completedExaminations
      ];

      const projectInfos: ProjectInfo[] = allExaminations.map(exam => ({
        id: exam.summaryId,
        title: exam.summaryTitle || '無題の評価',
        productCount: exam.summaryProductCount,
        criteriaCount: exam.summaryCriteriaCount,
        status: exam.summaryStatus,
        createdAt: exam.summaryCreatedAt,
        updatedAt: exam.summaryUpdatedAt
      }));

      setProjects(projectInfos);
    } catch (err) {
      setError('プロジェクト一覧の取得に失敗しました');
      console.error('Failed to load projects:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProjectToggle = (projectId: string) => {
    if (selectedIds.includes(projectId)) {
      setSelectedIds(selectedIds.filter(id => id !== projectId));
    } else if (selectedIds.length < 2) {
      setSelectedIds([...selectedIds, projectId]);
    }
  };

  const handleConfirm = () => {
    if (selectedIds.length === 2) {
      const selectedProjects = projects.filter(p => selectedIds.includes(p.id));
      onSelect(
        [selectedIds[0], selectedIds[1]],
        [selectedProjects[0].title, selectedProjects[1].title]
      );
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'InProgress': return '進行中';
      case 'Completed': return '完了';
      default: return '不明';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'InProgress': return '#3b82f6';
      case 'Completed': return '#10b981';
      default: return '#6b7280';
    }
  };

  if (isLoading) {
    return (
      <div className="project-selection">
        <div className="loading">
          <div className="loading-spinner" />
          <p>プロジェクト一覧を読み込み中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="project-selection">
        <div className="error">
          <p>{error}</p>
          <Button onClick={loadProjects}>再試行</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="project-selection">
      <div className="selection-header">
        <h2>統合するプロジェクトを選択</h2>
        <p>2つのプロジェクトを選択してください。選択されたプロジェクトが統合されます。</p>
        <div className="selection-counter">
          {selectedIds.length}/2 プロジェクト選択済み
        </div>
      </div>

      <div className="projects-grid">
        {projects.map(project => (
          <div
            key={project.id}
            className={`project-card ${selectedIds.includes(project.id) ? 'selected' : ''}`}
            onClick={() => handleProjectToggle(project.id)}
          >
            <div className="project-header">
              <h3>{project.title}</h3>
              <div className="project-status">
                <span 
                  className="status-badge"
                  style={{ backgroundColor: getStatusColor(project.status) }}
                >
                  {getStatusText(project.status)}
                </span>
              </div>
            </div>

            <div className="project-stats">
              <div className="stat">
                <span className="stat-label">評価対象</span>
                <span className="stat-value">{project.productCount}件</span>
              </div>
              <div className="stat">
                <span className="stat-label">観点</span>
                <span className="stat-value">{project.criteriaCount}件</span>
              </div>
            </div>

            <div className="project-dates">
              <div className="date-info">
                <span className="date-label">作成:</span>
                <span>{new Date(project.createdAt).toLocaleDateString('ja-JP')}</span>
              </div>
              <div className="date-info">
                <span className="date-label">更新:</span>
                <span>{new Date(project.updatedAt).toLocaleDateString('ja-JP')}</span>
              </div>
            </div>

            {selectedIds.includes(project.id) && (
              <div className="selection-indicator">
                <span className="selection-number">
                  {selectedIds.indexOf(project.id) + 1}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="selection-actions">
        {onCancel && (
          <Button variant="ghost" onClick={onCancel}>
            キャンセル
          </Button>
        )}
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={selectedIds.length !== 2}
        >
          統合を開始
        </Button>
      </div>

      <style>{`
        .project-selection {
          padding: 1rem;
        }

        .selection-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .selection-header h2 {
          font-size: 1.5rem;
          color: #1f2937;
          margin: 0 0 0.5rem 0;
        }

        .selection-header p {
          color: #6b7280;
          margin: 0 0 1rem 0;
        }

        .selection-counter {
          background: #dbeafe;
          color: #1e40af;
          padding: 0.5rem 1rem;
          border-radius: 0.375rem;
          font-weight: 600;
          display: inline-block;
        }

        .loading, .error {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 4rem 2rem;
          text-align: center;
        }

        .loading-spinner {
          width: 2rem;
          height: 2rem;
          border: 2px solid #e5e7eb;
          border-top: 2px solid #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 1rem;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .projects-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .project-card {
          background: white;
          border: 2px solid #e5e7eb;
          border-radius: 0.75rem;
          padding: 1.5rem;
          cursor: pointer;
          transition: all 0.2s;
          position: relative;
        }

        .project-card:hover {
          border-color: #3b82f6;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        .project-card.selected {
          border-color: #10b981;
          background: #f0fdf4;
          box-shadow: 0 4px 6px rgba(16, 185, 129, 0.2);
        }

        .project-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 1rem;
        }

        .project-header h3 {
          font-size: 1.125rem;
          font-weight: 600;
          color: #1f2937;
          margin: 0;
          flex: 1;
          line-height: 1.3;
        }

        .project-status {
          margin-left: 1rem;
        }

        .status-badge {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          border-radius: 9999px;
          font-size: 0.75rem;
          font-weight: 600;
          color: white;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .project-stats {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .stat {
          text-align: center;
        }

        .stat-label {
          display: block;
          font-size: 0.75rem;
          color: #6b7280;
          margin-bottom: 0.25rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .stat-value {
          display: block;
          font-size: 1.25rem;
          font-weight: 700;
          color: #1f2937;
        }

        .project-dates {
          padding-top: 1rem;
          border-top: 1px solid #e5e7eb;
        }

        .date-info {
          display: flex;
          justify-content: space-between;
          font-size: 0.75rem;
          color: #6b7280;
          margin-bottom: 0.25rem;
        }

        .date-label {
          font-weight: 500;
        }

        .selection-indicator {
          position: absolute;
          top: 0.75rem;
          right: 0.75rem;
          width: 1.5rem;
          height: 1.5rem;
          background: #10b981;
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .selection-actions {
          display: flex;
          justify-content: space-between;
          gap: 1rem;
          margin-top: 2rem;
        }

        @media (max-width: 768px) {
          .projects-grid {
            grid-template-columns: 1fr;
          }

          .project-header {
            flex-direction: column;
            gap: 0.5rem;
          }

          .project-status {
            margin-left: 0;
            align-self: flex-start;
          }

          .selection-actions {
            flex-direction: column-reverse;
          }
        }
      `}</style>
    </div>
  );
};